import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { LocationEditor } from "../components/LocationEditor";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "ninetyDayGoal", q: "What do you want in the next 90 days?", type: "choice",
    options: ["Extra cash", "First paid work", "Stop wasting money", "Simple saving habit (no tickers)", "Not sure"] },
  { key: "monthlyCash", q: "How much money per month can you put in?", type: "money" },
  { key: "hoursPerWeek", q: "How many hours per week are truly free?", type: "hours" },
  { key: "skills", q: "What are your skills, or what are you around a lot?", type: "text", placeholder: "e.g. good with tools, near a college, have a truck" },
  { key: "fears", q: "What's your biggest fear here?", type: "choice",
    options: ["Lose cash", "Look stupid", "Waste months", "Legal trouble"] },
  { key: "hasJob", q: "Do you already have a job or small business?", type: "choice",
    options: ["No", "Yes, a job", "Yes, a small business"] },
  { key: "location", q: "Where are you? (optional)", type: "location" },
  { key: "tools", q: "Any tools or space you already have? (optional)", type: "text", placeholder: "e.g. garage, ladder, sewing machine — or leave blank" },
];

export default function Intake() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const [ans, setAns] = useState({ monthlyCash: 0, hoursPerWeek: 0 });
  const [busy, setBusy] = useState(false);
  const step = STEPS[i];

  const set = (v) => setAns((a) => ({ ...a, [step.key]: v }));

  const next = async () => {
    if (i < STEPS.length - 1) { setI(i + 1); return; }
    // finish -> save profile then generate paths
    setBusy(true);
    try {
      await api.put("/profile", {
        ninetyDayGoal: ans.ninetyDayGoal, monthlyCash: Number(ans.monthlyCash) || 0,
        hoursPerWeek: Number(ans.hoursPerWeek) || 0, skills: ans.skills,
        fears: ans.fears, hasJob: ans.hasJob, tools: ans.tools,
      });
      nav("/paths");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  const skip = () => { if (i < STEPS.length - 1) setI(i + 1); else next(); };

  return (
    <Layout>
      <div className="max-w-xl mx-auto fadeup">
        <div className="flex items-center gap-2 mb-6">
          {STEPS.map((_, idx) => (
            <div key={idx} className="h-1 flex-1" style={{ background: idx <= i ? "var(--text)" : "var(--border-subtle)" }} />
          ))}
        </div>
        <div className="label-mono mb-2">Question {i + 1} of {STEPS.length}</div>
        <h2 className="font-head font-bold text-2xl sm:text-3xl mb-6" data-testid="intake-question">{step.q}</h2>

        {step.type === "choice" && (
          <div className="space-y-2" data-testid="intake-choices">
            {step.options.map((o) => (
              <button key={o} onClick={() => set(o)} data-testid={`intake-option-${o.replace(/\s+/g, "-").toLowerCase()}`}
                className="pm-card w-full text-left p-4 flex items-center justify-between"
                style={ans[step.key] === o ? { background: "var(--text)", color: "#fff" } : {}}>
                {o} {ans[step.key] === o && <ArrowRight className="w-4 h-4" />}
              </button>
            ))}
            <input className="pm-input mt-2" placeholder="Or type your own answer…"
              value={step.options.includes(ans[step.key]) ? "" : (ans[step.key] || "")}
              onChange={(e) => set(e.target.value)} data-testid="intake-choice-custom" />
          </div>
        )}

        {step.type === "money" && (
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xl">$</span>
              <input type="number" min="0" className="pm-input font-mono text-lg" value={ans.monthlyCash}
                onChange={(e) => set(e.target.value)} data-testid="intake-money" />
              <span className="text-sm" style={{ color: "var(--text-2)" }}>/ month</span>
            </div>
            <button onClick={() => set(0)} className="label-mono mt-3 underline" data-testid="intake-money-zero">I have $0 — use my time instead</button>
          </div>
        )}

        {step.type === "hours" && (
          <div className="flex items-center gap-2">
            <input type="number" min="0" className="pm-input font-mono text-lg" value={ans.hoursPerWeek}
              onChange={(e) => set(e.target.value)} data-testid="intake-hours" />
            <span className="text-sm" style={{ color: "var(--text-2)" }}>hours / week</span>
          </div>
        )}

        {step.type === "text" && (
          <textarea className="pm-input" rows={3} placeholder={step.placeholder}
            value={ans[step.key] || ""} onChange={(e) => set(e.target.value)} data-testid="intake-text" />
        )}

        {step.type === "location" && (
          <div>
            {user?.city ? (
              <div className="pm-card p-4 mb-3" data-testid="intake-location-set">
                Using: <b>{user.city}{user.region ? `, ${user.region}` : ""}</b>
                <span className="block label-mono mt-1">You can change this below or in Settings.</span>
              </div>
            ) : null}
            <LocationEditor />
            {!user?.city && (
              <p className="label-mono mt-3" data-testid="intake-location-optional">
                Optional — skip and we'll use general estimates. Add a city anytime for local prices & license links.
              </p>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-8 items-center">
          {i > 0 && <button onClick={() => setI(i - 1)} className="pm-btn-ghost flex items-center gap-2" data-testid="intake-back"><ArrowLeft className="w-4 h-4" /> Back</button>}
          <button onClick={skip} disabled={busy} className="pm-btn-ghost text-sm ml-auto" data-testid="intake-skip">Skip</button>
          <button onClick={next} disabled={busy} className="pm-btn flex items-center gap-2" data-testid="intake-next">
            {busy ? "Building paths..." : i === STEPS.length - 1 ? "See my paths" : "Next"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Layout>
  );
}
